const RequiredPackage = {
  stateKey: "required_package",

  state: {
    store_id: null,
    bundle_id: null,
    kit_id: null,
    kit_instance_no: null,
    parent_handle: null,
    parent_variant_id: null,
    add_to_cart: false,
    addon_handle: null,
    total_products: 1,
    metadata: {
      first_name: null,
      last_name: null,
      name: null,
      player_number: null,
    },
    parent_customization_fields: {
      name: false,
      number: false,
    },
    items: [],
    required_products: null,
    recommended_products: null,
  },

  init() {
    const newState = this.pullState();

    if (newState) {
      this.state = newState;
    }

    return this;
  },

  /**
   * This is to set an initial state for a kit.
   * @param {*} kit_id
   * @param {*} parent_handle
   * @param {*} parent_variant_id
   */
  async startRequiredPackage(overrides) {
    this.state = {
      ...this.state,
      ...overrides,
      bundle_id: crypto.randomUUID(),
    };

    this.pushState(); // To refresh the sessionStorage.
    // await this.initCartAttributes();
  },

  setMetadata(data) {
    this.state.metadata = {
      ...this.state.metadata,
      ...data,
    };

    this.pushState();
  },

  setParentCustomizationFields(data) {
    this.state.parent_customization_fields = {
      name: false,
      number: false,
      ...(this.state.parent_customization_fields || {}),
      ...data,
    };

    this.pushState();
  },

  /**
   * This is to fully remove the state.
   */
  killState() {
    this.wipeState();
    // this.wipeCartAttributes();
  },

  /**
   * We ensure the url has the kit id, otherwise we just clear the
   * state.
   */
  checkUrlForKit() {
    const urlParams = new URLSearchParams(window.location.search);
    const bundleId = urlParams.get("bundle") ?? null;

    if (!bundleId || bundleId != this.state.bundle_id) {
      this.killState();
    }
  },

  /**
   * Pulls the required package state.
   */
  pullState() {
    const state = sessionStorage.getItem(this.stateKey);

    if (state) {
      return JSON.parse(state);
    }

    return null;
  },

  /**
   * Updates the required package sessionStorage.
   */
  pushState() {
    sessionStorage.setItem(this.stateKey, JSON.stringify(this.state));
  },

  /**
   * Removes the required package state.
   */
  wipeState() {
    sessionStorage.removeItem(this.stateKey);
  },

  async initCartAttributes() {
    const cart = await fetch("/cart.js").then((res) => res.json());
    await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        attributes: {
          ...cart.attributes,
          _current_kit_id: this.state.kit_id,
          _current_kit_handle: this.state.parent_handle,
        },
      }),
    });
  },

  async wipeCartAttributes() {
    const cart = await fetch("/cart.js").then((res) => res.json());
    await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        attributes: {
          ...cart.attributes,
          _current_kit_id: null,
          _current_kit_handle: null,
        },
      }),
    });
  },

  addItem(
    productId,
    variantId,
    quantity = 1,
    properties,
    price,
    stepIndex = null,
  ) {
    // If we find another product with the same id, we remove it first.
    const index = this.state.items.findIndex(
      (item) => parseInt(item.stepIndex) === parseInt(stepIndex),
    );
    if (index !== -1) {
      this.state.items.splice(index, 1); // Remove 1 element at the found index
    }

    // then we add our new item
    this.state.items.push({
      productId: productId,
      variantId: variantId,
      quantity: quantity ?? 1,
      properties: properties,
      price: price,
      stepIndex: stepIndex,
    });

    this.state.items.sort(
      (a, b) => parseInt(a.stepIndex) - parseInt(b.stepIndex),
    );

    this.pushState();
  },

  getCounts() {
    return {
      total: this.state.total_products,
      added: this.state.items.length,
    };
  },

  getBundleString() {
    // Copiar items
    let itemsToInclude = [...this.state.items];

    // Si add_to_cart es true, agregar el padre al principio del bundle string
    if (this.state.add_to_cart && this.state.parent_variant_id) {
      // Crear propiedades del padre incluyendo metadata y _bundle_parent
      const firstName = String(this.state.metadata.first_name ?? "").trim();

      const lastName = String(this.state.metadata.last_name ?? "").trim();

      const name = String(this.state.metadata.name ?? "").trim();

      const playerNumber = String(
        this.state.metadata.player_number ?? "",
      ).trim();

      const parentProperties = {
        _bundle_parent: "true",
        _store_id: this.state.store_id,
        _kit_instance_no: this.state.kit_instance_no,
        ...(firstName ? { "first-name": firstName } : {}),
        ...(lastName ? { "last-name": lastName } : {}),
        ...(name ? { name: name } : {}),
        ...(playerNumber ? { "player-number": playerNumber } : {}),
      };

      itemsToInclude.unshift({
        variantId: this.state.parent_variant_id,
        quantity: 1,
        properties: parentProperties,
        price: 0,
      });
    }

    // Generar el bundle string con todos los items (incluido el padre si corresponde)
    const parentItem = itemsToInclude.find(
      (item) => String(item.properties?._bundle_parent) === "true",
    );

    // Get the existing store key from the bundle items.
    const sharedStoreKey = itemsToInclude
      .map((item) => item.properties?._store_key)
      .find((value) => value != null && String(value) !== "");

    const fullString = itemsToInclude
      .map((item) => {
        const toAdd = {
          ...item.properties,
        };

        if (parentItem) {
          // Keep store details on the parent only.
          delete toAdd._store_id;
          delete toAdd._store_key;

          if (item === parentItem) {
            toAdd._store_id = this.state.store_id;

            if (sharedStoreKey != null) {
              toAdd._store_key = sharedStoreKey;
            }
          }
        } else {
          // Preserve existing behavior when there is no parent.
          toAdd._store_id = this.state.store_id;
        }

        const props = Object.keys(toAdd)
          .map((key) => `${key}=~u~${encodeURIComponent(String(toAdd[key]))}`)
          .join("&");

        return `${item.variantId},${item.quantity > 1 ? item.quantity : ""},${props},${item.price}`;
      })
      .join("|");

    return fullString
      .match(new RegExp(`.{1,${255}}`, "g"))
      .reduce((acc, substring, index) => {
        return {
          ...acc,
          [`_bundle_string_${index + 1}`]: substring,
        };
      }, {});
  },

  getBundlePayload() {
    const firstName = String(this.state.metadata.first_name ?? "").trim();

    const lastName = String(this.state.metadata.last_name ?? "").trim();

    const name = String(this.state.metadata.name ?? "").trim();

    const playerNumber = String(this.state.metadata.player_number ?? "").trim();

    return {
      id: this.state.parent_variant_id,
      quantity: 1,
      properties: {
        ...(firstName ? { "first-name": firstName } : {}),
        ...(lastName ? { "last-name": lastName } : {}),
        ...(name ? { name: name } : {}),
        ...(playerNumber ? { "player-number": playerNumber } : {}),
        _kit_instance_no: this.state.kit_instance_no,
        _bundle_id: this.state.bundle_id,
        ...this.getBundleString(),
      },
    };
  },
};

// This is where we init the state, and we use the window
// object to keep the copy in there.
window.RequiredPackage = RequiredPackage.init();

// With this, on every page load we check if we're within a
// kit ... if we don't ... let's just kill the state.
window.RequiredPackage.checkUrlForKit();

(function ($) {
  const PLAYER_NAME_PROPERTY_KEYS = new Set([
    "player-name",
    "player-first-name",
    "player-last-name",
    "first-name",
    "last-name",
    "name",
  ]);

  const PLAYER_NUMBER_PROPERTY_KEYS = new Set(["player-number", "number"]);

  function normalizePlayerPropertyKey(input) {
    const propertyName = input
      .getAttribute("name")
      ?.match(/^properties\[(.+?)\]$/)?.[1];

    return String(propertyName || "")
      .trim()
      .toLowerCase()
      .replaceAll("_", "-")
      .replaceAll(" ", "-");
  }

  function sanitizePlayerNumberInput(input) {
    input.value = String(input.value || "")
      .replace(/\D/g, "")
      .slice(0, 5);
  }

  function applyPlayerFieldValidation(root = document) {
    root.querySelectorAll('input[name^="properties["]').forEach((input) => {
      const propertyKey = normalizePlayerPropertyKey(input);

      if (PLAYER_NAME_PROPERTY_KEYS.has(propertyKey)) {
        input.maxLength = 30;
      }

      if (PLAYER_NUMBER_PROPERTY_KEYS.has(propertyKey)) {
        input.maxLength = 5;
        input.inputMode = "numeric";
        input.pattern = "[0-9]*";
        sanitizePlayerNumberInput(input);
      }
    });
  }

  document.addEventListener("input", (event) => {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    const propertyKey = normalizePlayerPropertyKey(event.target);

    if (PLAYER_NAME_PROPERTY_KEYS.has(propertyKey)) {
      event.target.value = String(event.target.value || "").slice(0, 30);
    }

    if (PLAYER_NUMBER_PROPERTY_KEYS.has(propertyKey)) {
      sanitizePlayerNumberInput(event.target);
    }
  });

  document.addEventListener(
    "submit",
    () => {
      applyPlayerFieldValidation();
    },
    true,
  );

  function hideParentCustomizationFieldsOnBundleChild() {
    const bundleId = new URLSearchParams(window.location.search).get("bundle");

    if (!bundleId || bundleId !== RequiredPackage.state.bundle_id) {
      return;
    }

    const parentFields = RequiredPackage.state.parent_customization_fields || {};

    document
      .querySelectorAll('.custom-fields input[name^="properties["]')
      .forEach((input) => {
        const propertyKey = normalizePlayerPropertyKey(input);
        const hideName =
          parentFields.name && PLAYER_NAME_PROPERTY_KEYS.has(propertyKey);
        const hideNumber =
          parentFields.number && PLAYER_NUMBER_PROPERTY_KEYS.has(propertyKey);

        if (hideName || hideNumber) {
          input.closest(".field-container")?.remove();
        }
      });
  }

  $(() => {
    hideParentCustomizationFieldsOnBundleChild();
    applyPlayerFieldValidation();
  });

  function getPropertyValue(selectors) {
    for (const selector of selectors) {
      const value = document.querySelector(selector)?.value?.trim();

      if (value) {
        return value;
      }
    }

    return null;
  }

  function captureBundleParentCustomization() {
    const parentFields = {
      name: false,
      number: false,
    };

    document
      .querySelectorAll('.custom-fields input[name^="properties["]')
      .forEach((input) => {
        const propertyKey = normalizePlayerPropertyKey(input);

        if (PLAYER_NAME_PROPERTY_KEYS.has(propertyKey)) {
          parentFields.name = true;
        }

        if (PLAYER_NUMBER_PROPERTY_KEYS.has(propertyKey)) {
          parentFields.number = true;
        }
      });

    RequiredPackage.setParentCustomizationFields(parentFields);

    RequiredPackage.setMetadata({
      first_name: getPropertyValue([
        'input[name="properties[first-name]"]',
        'input[name="properties[first_name]"]',
        'input[name="properties[First Name]"]',
        'input[name="properties[player-first-name]"]',
        'input[name="properties[player_first_name]"]',
        'input[name="properties[Player First Name]"]',
      ]),
      last_name: getPropertyValue([
        'input[name="properties[last-name]"]',
        'input[name="properties[last_name]"]',
        'input[name="properties[Last Name]"]',
        'input[name="properties[player-last-name]"]',
        'input[name="properties[player_last_name]"]',
        'input[name="properties[Player Last Name]"]',
      ]),
      name: getPropertyValue([
        'input[name="properties[name]"]',
        'input[name="properties[Name]"]',
        'input[name="properties[player-name]"]',
        'input[name="properties[player_name]"]',
        'input[name="properties[Player Name]"]',
      ]),
      player_number: getPropertyValue([
        'input[name="properties[player-number]"]',
        'input[name="properties[player_number]"]',
        'input[name="properties[Player Number]"]',
        'input[name="properties[number]"]',
        'input[name="properties[Number]"]',
      ]),
    });
  }

  function addParentCustomizationToComponent(properties, customizationValue) {
    const customization = String(customizationValue || "")
      .trim()
      .toLowerCase();

    if (!customization) {
      return;
    }

    const metadata = RequiredPackage.state.metadata || {};

    if (customization.includes("name")) {
      if (metadata.first_name) properties["first-name"] = metadata.first_name;
      if (metadata.last_name) properties["last-name"] = metadata.last_name;
      if (metadata.name) properties.name = metadata.name;
    }

    if (customization.includes("number") && metadata.player_number) {
      properties["player-number"] = metadata.player_number;
    }
  }

  /**
   * Add the complete bundle to cart with all components serialized
   */
  function addBundleToCart() {
    // Get addon if exists
    let addon = sessionStorage.getItem("bundle_addon");
    addon = addon ? JSON.parse(addon) : null;

    // Get bundle metadata from sessionStorage
    const metadata = RequiredPackage.state;
    if (!metadata.kit_id || !metadata.parent_variant_id) {
      console.error("Bundle metadata is incomplete in sessionStorage");
      return;
    }

    // POST to cart
    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(RequiredPackage.getBundlePayload()),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status == "422" && data?.message != null) {
          alert(data.message);

          return;
        }
        // Update cart attributes with package count
        fetch("/cart.js")
          .then((response) => response.json())
          .then((cartData) => {
            // We get all the bundles in cart.
            const packagesInCart = cartData.items.filter(
              (item) => item.properties && item.properties["_bundle_string_1"],
            ).length;

            // We update the packages in cart attribute.
            return fetch(window.Shopify.routes.root + "cart/update.js", {
              method: "POST",
              headers: {
                "Content-type": "application/json",
              },
              body: JSON.stringify({
                attributes: {
                  ...cartData.attributes,
                  _required_packages_in_cart: packagesInCart.toString(),
                },
              }),
            });
          })
          .then(() => {
            // Check if we should redirect to thank you page
            const metadata = RequiredPackage.state;
            const shouldShowThankYou = metadata.addon_handle !== null;

            // Redirect to cart or thank you page
            if (shouldShowThankYou) {
              // Here we don't kill the metadata in case we need to readd the bundle because of the addon.

              window.location.href =
                "/pages/required-packages-thank-you?bundle=" +
                RequiredPackage.state.bundle_id;
            } else {
              // if there's nothing more to do, we just kill the state and go to the cart.
              RequiredPackage.killState();

              window.location.href = "/cart";
            }
          })
          .catch((error) => {
            console.error("Error updating cart attributes:", error);
          });
      })
      .catch((error) => {
        console.error("Error adding bundle to cart:", error);
      });
  }

  /**
   * Main function to handle adding kit products
   * Now collects components instead of adding individually
   */
  $.fn.addRequiredPackageProductToCart = function (addKitToCart = false) {
    let btn = $(this);
    let cont = btn.closest(".required-packages-container");
    let productId = cont.attr("data-productid");
    let qty = parseInt(cont.attr("data-requiredQuantity"));
    let form = $(".product-form form.shopify-product-form");
    let qty_val = parseInt(form.find('input[name="quantity"]').val());

    let valid = validateProductForm(btn.get(0));
    if (!valid) {
      return;
    }

    if (cont.hasClass("tpl-package")) {
      window.location.href = btn.attr("href");
      return;
    } else if (window.location.href.indexOf("cart") > -1) {
      $(".Cart__Checkout").trigger("click");
      return;
    }

    if (qty_val < qty) {
      form.find('input[name="quantity"]').val(qty);
      qty_val = qty;
    }

    /**
     * These configurations allow us to pull the first and last name from the
     * master product when we start the process.
     */
    if (addKitToCart) {
      // Capture customization entered on the bundle parent before redirecting.
      captureBundleParentCustomization();
      window.location.href =
        btn.attr("href") + `?bundle=${RequiredPackage.state.bundle_id}&step=0`;
      return;
    }

    // Get variant ID
    let variantId = form.find('input[name="id"]').val();

    window.syncSelectedStoreImage(form[0]);

    // We get all the properties for this line item, but we need to filter some
    const properties = form
      .find('[name^="properties["]:not(:disabled)')
      .toArray()
      .reduce((acc, input) => {
        key = input.getAttribute("name").match(/properties\[(.+?)\]/)?.[1];

        if ([null, "", "null"].includes(input.value)) {
          return acc;
        }

        return {
          ...acc,
          [key]: input.value,
        };
      }, {});

    form
      .find('[name^="option"')
      .toArray()
      .forEach((input) => {
        properties[input.getAttribute("name")] = input.value;
      });

    const baseUrl = window.location.href.split("?")[0];

    fetch(baseUrl + ".json")
      .then((res) => res.json())
      .then(async (res) => {
        const price = res.product.variants.find((v) => {
          return v.id == parseInt(variantId);
        }).price;

        const urlParams = new URLSearchParams(window.location.search);
        const currentStep = parseInt(urlParams.get("step") ?? 0);

        // try {
        //   const artworkResponse = await fetch(
        //     `https://dev.3n2sports.com/api/artwork/find?store_id=${RequiredPackage.state.store_id}&product_id=${productId}&variant_id=${variantId}`,
        //   );

        //   const artworkData = await artworkResponse.json();

        //   if (artworkData.artwork_id) {
        //     properties["artwork_id"] = artworkData.artwork_id;
        //   }
        // } catch (error) {
        //   console.error("Item artwork fetch failed", error);
        // }

        addParentCustomizationToComponent(
          properties,
          cont.attr("data-player-customization") || "",
        );

        properties["_bundle_kit_product"] = "true";
        // properties['_bundle_pt_id'] = RequiredPackage.state.parent_variant_id || '';
        properties["_kit_instance_no"] = RequiredPackage.state.kit_instance_no;

        RequiredPackage.addItem(
          productId,
          variantId,
          parseInt(qty_val ?? 1),
          properties,
          price,
          currentStep,
        );

        if (btn.attr("data-last-item") === "true") {
          addBundleToCart();
        } else {
          window.location.href = btn.attr("href");
        }
      });
  };

  /**
   * Helper function to safely parse JSON from HTML attributes
   * Handles HTML-escaped JSON strings
   */
  function parseJsonAttribute(attrValue) {
    if (!attrValue) return null;
    try {
      // Create a temporary element to decode HTML entities
      const textarea = document.createElement("textarea");
      textarea.innerHTML = attrValue;
      const decoded = textarea.value;
      return JSON.parse(decoded);
    } catch (e) {
      console.error("Error parsing JSON attribute:", e, attrValue);
      return null;
    }
  }

  $(document).on("click", ".add-kit-to-cart", function (e) {
    e.preventDefault();
    let el = $(this).get(0);

    // Clear any existing bundle data
    RequiredPackage.killState();

    // Generate kit metadata
    const kitId = $(this).attr("data-kitid");
    const parentVariantId = $(this).attr("data-prodid");

    if (!validateProductForm(el)) {
      e.stopPropagation();
      return false;
    }

    /**
     * Adding the initial product sets the initial state. from here
     * We'll keep collecting all products till we finish adding
     * them all. Then the state is removed.
     */
    const addonHandle = $(this).attr("data-addon-handle") || null;
    const kitInstanceNo =
      parseInt(sessionStorage.getItem("kit_instance_no") || "0") + 1;
    sessionStorage.setItem("kit_instance_no", kitInstanceNo);

    RequiredPackage.startRequiredPackage({
      store_id: $(this).attr("data-store-id"),
      kit_id: kitId,
      kit_instance_no: kitInstanceNo,
      parent_handle: $(this).attr("data-handle") || null,
      parent_variant_id: parentVariantId || null,
      add_to_cart: $(this).attr("data-add-to-cart") === "true",
      total_products:
        (parseInt($(this).attr("data-products-count")) || 0) +
        (addonHandle ? 1 : 0),
      addon_handle: addonHandle,
      recommended_products: parseJsonAttribute(
        $(this).attr("data-recommended-products"),
      ),
      required_products: parseJsonAttribute($(this).attr("data-kit-products")),
    }).then(() => {
      $(this).addRequiredPackageProductToCart(true);
    });
  });

  $(document).on(
    "click",
    ".add-required-package-product-to-cart",
    function (e) {
      let valid = validateProductForm(e.target);
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!valid) {
        return false;
      } else {
        $(this).addRequiredPackageProductToCart();
      }
    },
  );

  $(document).on(
    "click",
    ".CartItem.package-item .CartItem__Remove",
    function (e) {
      e.preventDefault();

      jQuery.ajax({
        type: "GET",
        url: "/cart/clear.js",
        dataType: "json",
        success: function () {
          RequiredPackage.killState();
          window.location.href = "/cart";
        },
      });
    },
  );

  $(document).on("click", ".required-package-addon-cancel", function (e) {
    e.preventDefault();

    // Add bundle to cart
    addBundleToCart();
  });

  $(document).on("click", ".required-package-addon-add", function (e) {
    e.preventDefault();

    let form = e.target.closest("form");

    let variantId = form.querySelector('input[name="id"]').value;
    let qty = form.querySelector('input[name="quantity"]')?.value ?? 1;

    // Collect addon properties
    let formData = new FormData(form);
    let properties = {
      _bundle_addon: true,
    };

    for (let [key, value] of formData.entries()) {
      if (
        key === "id" ||
        key === "product_id" ||
        key === "quantity" ||
        key === "sections"
      ) {
        continue;
      }

      let match = key.match(/properties\[(.+)\]/);
      if (match) {
        properties[match[1]] = value;
      } else if (key.startsWith("option")) {
        properties[key] = value;
      }
    }

    fetch(`/products/${RequiredPackage.state.addon_handle}.json`)
      .then((res) => res.json())
      .then((res) => {
        const price = res.product.variants.find((v) => v.id == variantId).price;

        // we use a productID of zero because we don't care much about it.
        RequiredPackage.addItem(
          res.id,
          variantId,
          parseInt(qty),
          properties,
          price,
        );

        // Add bundle to cart
        addBundleToCart();
      });
  });

  $(document).on(
    "click",
    ".required-packages-add-recommended-to-cart",
    function (e) {
      e.preventDefault();
      e.stopPropagation();
      submitRequiredPackageRecommended($(this));
      return false;
    },
  );

  function submitRequiredPackageRecommended(button) {
    let form = button.closest("form")[0];
    let formData = new FormData(form);

    let variants = form.closest(".product-card").dataset.variants;
    if (variants != null) variants = JSON.parse(variants);

    // get options and qty
    let values = [];
    let qty = 1;
    for (let pair of formData.entries()) {
      if (pair[0] === "Size" || pair[0] === "Color") {
        values.push(pair[1]);
      }
      if (pair[0] === "quantity") qty = pair[1];
    }

    // get variant ID
    let variantId = null;
    if (variants != null) {
      variants.forEach(function (variant) {
        let useThisVariant = true;
        values.forEach(function (value) {
          if (!variant.options.includes(value)) {
            useThisVariant = false;
          }
        });

        if (useThisVariant) {
          variantId = variant.id;
        }
      });
    }

    if (variantId != null) {
      form.querySelector('input[name="id"]').value = variantId;

      // submit and show mini cart
      const productForm = new FormData(form);
      productForm.append("sections", "mini-cart");
      productForm.delete("option1");
      productForm.delete("option2");
      productForm.delete("option3");
      const response = fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
        body: productForm,
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status != 422) {
            // Fetch updated cart to get sections and open drawer
            fetch("/cart?view=json")
              .then((res) => res.json())
              .then((response) => {
                const cartContent = response;
                cartContent["sections"] = response["sections"];

                document.documentElement.dispatchEvent(
                  new CustomEvent("cart:refresh", {
                    bubbles: true,
                    detail: {
                      cart: cartContent,
                      openMiniCart: true,
                    },
                  }),
                );
              });

            form.dispatchEvent(
              new CustomEvent("cart-notification:show", {
                bubbles: true,
                cancelable: true,
                detail: {
                  status: "success",
                },
              }),
            );
          } else {
            form.dispatchEvent(
              new CustomEvent("cart-notification:show", {
                bubbles: true,
                cancelable: true,
                detail: {
                  status: data.ok ? "success" : "error",
                  error: data.description || "",
                },
              }),
            );
          }
        })
        .catch((error) => {
          console.error("Error adding product to cart:", error);
          form.dispatchEvent(
            new CustomEvent("cart-notification:show", {
              bubbles: true,
              cancelable: true,
              detail: {
                status: "error",
                error: "Failed to add product to cart",
              },
            }),
          );
        });
    }
  }
})(jQuery);

document.addEventListener("DOMContentLoaded", (e) => {
  if (RequiredPackage.state.kit_id == null) {
    document
      .querySelector("body")
      .classList.remove("required-package-initialized");
  } else if (document.querySelector(".required-packages-thank-you") != null) {
    initRequiredPackageThankYou();
  } else {
    let isParent = false;
    if (document.querySelector("body").classList.contains("is-kit-product")) {
      isParent = true;
    }
    initRequiredPackage(false, isParent);
  }
});

function initRequiredPackageThankYou() {
  const metadata = RequiredPackage.state;
  let handle = metadata.parent_handle;
  fetch("/products/" + handle + ".json")
    .then((response) => response.json())
    .then((data) => {
      parseRequiredPackageThankYou(data.product);
    })
    .catch((error) => {
      console.log(error);
    });
}

function parseRequiredPackageThankYou(product) {
  let recommended = RequiredPackage.state.recommended_products;
  let img = document.querySelector(
    ".required-packages-thank-you .product-image img",
  );
  let recommendedContainer = document.querySelector(
    ".required-packages-thank-you .required-packages-recommended",
  );
  img.setAttribute("src", product.image.src);
  img.setAttribute("alt", product.image.alt);

  let tpl = document.querySelector(
    ".required-packages-thank-you .product-card-tpl",
  );

  if (recommended != null) {
    recommended.forEach(function (prod) {
      let clone = tpl.cloneNode(true);
      clone.classList.remove("product-card-tpl");
      clone.classList.add("product-card");
      clone.dataset.handle = prod.handle;
      clone.dataset.variants = JSON.stringify(prod.variants);
      clone.querySelector("img").setAttribute("src", prod.featured_image);
      clone.querySelector(".title").innerHTML = prod.title;

      if (prod.variants != null && prod.variants.length > 1) {
        let optionsData = parseRequiredPackageThankYouOptions(prod);

        // append options to dropdowns
        for (const optionName in optionsData) {
          if (optionsData.hasOwnProperty(optionName)) {
            let values = optionsData[optionName];
            let targetCont = clone.querySelector(".field." + optionName);
            let target = targetCont.querySelector("select");

            values.forEach(function (value) {
              let opt = document.createElement("option");
              opt.setAttribute("value", value);
              opt.innerHTML = value;
              target.appendChild(opt);
            });

            targetCont.classList.remove("hidden");
          }
        }
      }

      recommendedContainer.appendChild(clone);
    });
  }
}

function parseRequiredPackageThankYouOptions(prod) {
  let data = {};

  let i = 1;
  prod.options.forEach(function (option) {
    if (typeof data[option] == "undefined") {
      data[option] = [];
    }

    prod.variants.forEach(function (variant) {
      if (!data[option].includes(variant["option" + i])) {
        data[option].push(variant["option" + i]);
      }
    });

    i++;
  });

  return data;
}

/**
 * Starts the required package product process. This loads on every product page
 * to identify the kind of product we're dealing with and then it renders the
 * proper information
 * @param {*} redirect
 * @param {*} isParent
 * @returns
 */
function initRequiredPackage(redirect = false, isParent = false) {
  let container = document.querySelector(".required-packages-container");

  if (!container) {
    return;
  }

  let products = RequiredPackage.state.required_products || [];
  const urlParams = new URLSearchParams(window.location.search);

  let currentIndex = parseInt(urlParams.get("step"));

  // fallback only if step is missing
  if (isNaN(currentIndex)) {
    let productId = String(container.dataset.productid);
    currentIndex = products.findIndex(
      (p) => String(p.product.id) === productId,
    );
  }

  let isAddon =
    RequiredPackage.state.addon_handle &&
    window.location.href.indexOf(
      "products/" + RequiredPackage.state.addon_handle,
    ) > -1;

  if (isAddon) {
    document.querySelector("body").classList.add("is-kit-addon");
    currentIndex = products.length;
    isAddon = true;
  }

  parseRequiredPackage(currentIndex, redirect, isAddon, isParent);
}

function parseRequiredPackage(currentIndex, redirect, isAddon, isParent) {
  let container = document.querySelector(".required-packages-container");
  const products = RequiredPackage.state.required_products;
  const components = RequiredPackage.state.items;
  let productId = parseInt(container.dataset.productid);
  const metadata = RequiredPackage.state;

  // Get current progress from sessionStorage
  let current = components.length;
  let prevItem = null;
  let nextItem = null;
  let currentItem = null;
  let total = RequiredPackage.state.total_products;
  let step = currentIndex + 1;
  let prevVariant = null;

  if (currentIndex != null && !isAddon) {
    currentItem = products[currentIndex];
    if (currentIndex > 0) {
      prevItem = products[currentIndex - 1];
    }

    if (step <= products.length) {
      nextItem = products[step];
    }
  } else {
    prevItem = products[products.length - 1];
    nextItem = null;
  }

  // We get the variant to add it to the url.
  if (prevItem != null && currentIndex > 0) {
    prevVariant =
      RequiredPackage?.state?.items.find(
        (item) => parseInt(item.stepIndex) === parseInt(currentIndex - 1),
      )?.variantId ?? null;
  }

  let btn = container.querySelector(".add-required-package-product-to-cart");

  // Set the correct URL, depending which step we're on
  if (nextItem) {
    btn.setAttribute(
      "href",
      `/products/${nextItem.product.handle}?bundle=${RequiredPackage.state.bundle_id}&step=${step}`,
    );
    btn.dataset.lastItem = "false";
  } else {
    if (metadata.addon_handle != null) {
      btn.setAttribute(
        "href",
        `/products/${metadata.addon_handle}?bundle=${RequiredPackage.state.bundle_id}&step=${products.length}`,
      );
      btn.dataset.lastItem = "false";
    } else {
      btn.setAttribute("href", "/cart");
      btn.dataset.lastItem = "true";
    }
  }

  // If we're not on the first item, set up the back button
  if ((prevItem && currentIndex > 0) || (prevItem && currentIndex == null)) {
    container
      .querySelector(".required-package-back")
      .setAttribute(
        "href",
        `/products/${prevItem.product.handle}?bundle=${RequiredPackage.state.bundle_id}&step=${currentIndex - 1}&variant=${prevVariant ?? ""}`,
      );
    container
      .querySelector(".required-package-back")
      .classList.remove("hidden");
  }

  // Set up required quantity
  if (currentItem) {
    container.dataset.requiredQuantity = parseInt(currentItem.quantity);
    const qtySelector = document.querySelector(
      ".product-form .quantity-selector__input",
    );

    if (qtySelector) {
      qtySelector.setAttribute("value", parseInt(currentItem.quantity));
      qtySelector.setAttribute("min", parseInt(currentItem.quantity));
    }
  }

  fetch(window.location.href.split("?").shift() + ".json")
    .then((res) => res.json())
    .then((res) => {
      const addedItem = RequiredPackage.state.items.find(
        (item) => parseInt(item.stepIndex) === parseInt(currentIndex),
      );

      if (!addedItem) {
        return;
      }

      const variant = res.product.variants.find(
        (v) => String(v.id) === String(addedItem.variantId),
      );

      // We restore any existing props that we might have had.
      Object.keys(addedItem.properties || {}).forEach((key) => {
        const input = document.querySelector(
          `input[name="properties[${key}]"]`,
        );
        if (input) {
          input.value = addedItem.properties[key];
        }
      });

      // Properly restore variant selection using the theme's variant selection system
      const productVariantsElement = document.querySelector("product-variants");
      if (productVariantsElement && variant) {
        productVariantsElement.selectVariant(variant.id);
      }
    });

  // Set up progress container
  let progressContainer = container.querySelector(".progress-container");
  // When on addon page, show products.length + 1 as current step
  let currentStep = isAddon ? products.length + 1 : currentIndex + 1;
  progressContainer.querySelector(".current").innerHTML = currentStep;
  progressContainer.querySelector(".total").innerHTML =
    RequiredPackage.getCounts().total;
  progressContainer.querySelector(".percentage .bg").style.width =
    (currentStep / total) * 100 + "%";

  // Redirect if necessary
  if (redirect) {
    if (nextItem)
      window.location.href =
        "/products/" +
        nextItem.product.handle +
        "?bundle=" +
        RequiredPackage.state.bundle_id;
    else if (prevItem)
      window.location.href =
        "/products/" +
        prevItem.product.handle +
        "?bundle=" +
        RequiredPackage.state.bundle_id;
  }
}

document
  .querySelectorAll(".quantity-selector__button.is-disabled")
  .forEach((el) => {
    el.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    });
  });